import React, { Component } from 'react';
import { isEqual } from 'lodash';
import PropTypes from 'prop-types';
import ExportableTable from 'containers/tables/ExportableTable';
import 'react-table-6/react-table.css';
import ContentTile from 'components/atoms/layout/ContentTile';
import ContentHeader from 'components/atoms/layout/ContentHeader';
import CellDiv from 'components/molecules/tables/components/CellDiv';
import helpers from 'helpers';
import RenderingTile from 'components/molecules/RenderingTile';
import Pagination, { RowsPerPageDefault } from 'components/molecules/tables/components/Pagination';
import { insertBeforeItem, insertAfterItem } from 'helpers/utilities';
import { generateFooterData, generateHeaders } from 'helpers/tables';

const DEFAULT_PLACEMENT_ACCESSORS = ['placement', 'impressions', 'ecpa_activities', 'ecpa', 'spend'];
const DEFAULT_TARGETING_TACTIC_ACCESSORS = ['targetingTactic', 'impressions', 'ecpa_activities', 'ecpa'];

class PlacementsTable extends Component {
    constructor(props) {
        super(props);

        let columns;
        let data;

        if (props.hasData) {
            const table = PlacementsTable.createTableData(props.data, props);
            columns = table.columns;
            data = table.data;
        }

        this.state = {
            data,
            columns,
            hasData: props.hasData,
            showTotalActivities: props.showTotalActivities,
            exportName: props.exportName,
            params: (this.props.params || {}),
        };
    }

    shouldComponentUpdate(nextProps) {
        return !isEqual(nextProps, this.props);
    }

    static createTableData(data, props) {
        const headers = generateHeaders({
            keys: [
                'impressions', 'total_activities', 'ecpa_activities', 'startedPlays',
                'completed_plays', 'completion_rate', 'clicks', 'ctr', 'cpcv', 'ecpa', 'conversion_revenue', 'spend'
            ],
            footerData: generateFooterData({ data, filteredKeys: ['placementName', 'targetingTacticName'] }),
            colOptions: {
                labelCode: props.entityObjectiveLabelCode
            },
            initial: {
                placement: {
                    Header: 'PLACEMENT',
                    accessor: 'placementName',
                    Cell: row => <CellDiv value={row.value} />,
                    Footer: (
                        <CellDiv value='Total' strong />
                    )
                },
                targetingTactic: {
                    Header: 'TACTIC',
                    accessor: 'targetingTacticName',
                    Cell: row => <CellDiv value={row.value} />,
                    Footer: (
                        <CellDiv value='Total' strong />
                    )
                },
            }
        });

        let defaultAccessors;
        switch (props.tableType) {
        case 'targetingTactics':
            defaultAccessors = DEFAULT_TARGETING_TACTIC_ACCESSORS;
            break;
        case 'placements':
        default:
            defaultAccessors = DEFAULT_PLACEMENT_ACCESSORS;
        }

        let { accessors = defaultAccessors } = props;

        if (props.showTotalActivities && !accessors.includes('total_activities')) {
            accessors = insertBeforeItem(accessors, 'ecpa_activities', 'total_activities');
        }

        if (props.showConversionRevenue && !accessors.includes('conversion_revenue')) {
            accessors = insertAfterItem(accessors, 'spend', 'conversion_revenue');
        }

        const columns = accessors.map(accessor => headers[accessor]);
        return { data, columns };
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const nextData = nextProps.data;
        const nextParams = nextProps.params;
        const nextExportName = nextProps.exportName;
        const nextHasData = nextProps.hasData;
        const nextShowTotalActivities = nextProps.showTotalActivities;
        const nextShowConversionRevenue = nextProps.showConversionRevenue;
        let nextState = null;

        if (nextParams !== prevState.params
            || nextData !== prevState.data
            || nextShowTotalActivities !== prevState.showTotalActivities
        ) {
            let columns = [];
            let data = [];

            if (nextHasData) {
                const table = PlacementsTable.createTableData(nextData, nextProps);
                columns = table.columns;
                data = table.data;
            }

            nextState = {
                ...prevState,
                columns,
                data,
                exportName: nextExportName,
                showTotalActivities: nextShowTotalActivities,
                showConversionRevenue: nextShowConversionRevenue,
                hasData: nextHasData,
                params: nextParams
            };
        }
        return nextState;
    }
    render() {
        const { data, exportName, columns, hasData } = this.state;

        return hasData ? (
            <ContentTile padding={helpers.contentPadding('table')}>
                <ContentHeader tableData={data} exportName={exportName} type={'table'}>{this.props.title}</ContentHeader>
                <ExportableTable
                    key={1}
                    title={this.props.title}
                    exportName={exportName}
                    data={data}
                    columns={columns}
                    getTheadThProps={() => ({ style: { outline: 0 } })}
                    getTfootProps={() => ({ style: { backgroundColor: '#E2E2E2', fontWeight: 'bold', boxShadow: 'none' } })}
                    defaultSorted={this.props.defaultSorted || [
                        {
                            id: 'ecpa',
                            desc: true
                        }
                    ]}
                    showPagination={data.length > RowsPerPageDefault}
                    PaginationComponent={Pagination}
                    defaultPageSize={RowsPerPageDefault}
                    minRows={data.length < RowsPerPageDefault ? data.length : RowsPerPageDefault}
                    className="-highlight"
                />
            </ContentTile>
        ) : (
            <RenderingTile height='400px' show={hasData} />
        );
    }
}

PlacementsTable.propTypes = {
    exportName: PropTypes.string.isRequired,
    data: PropTypes.arrayOf(PropTypes.shape({
        placementName: PropTypes.string,
        impressions: PropTypes.number,
        ecpa_activities: PropTypes.number,
        ecpa: PropTypes.number
    })),
    title: PropTypes.string.isRequired,
    accessors: PropTypes.arrayOf(PropTypes.string),
    hasData: PropTypes.bool,
    entityObjectiveLabelCode: PropTypes.string
};

export default PlacementsTable;



// WEBPACK FOOTER //
// ./src/components/molecules/tables/PlacementsTable.js